import React, { useCallback } from "react";
import { DriveGear, VehicleStatus } from "@optimusride/client-library/@types/grpc/vehicle_status";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import InfoIcon from "@material-ui/icons/Info";
import { IconButton, Tooltip, withStyles } from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { useDispatch, useSelector } from "react-redux";
import Battery from "../assets/Battery";
import VehicleMarker, { VEHICLE_COLOR_MAPPING } from "../assets/VehicleMarker";
import {
    SPACING_1,
    SPACING_8,
    SPACING_12,
    SPACING_32,
    SPACING_56,
    SPACING_4,
} from "../../common/styles/spacing";
import { FONT_0_625, FONT_1 } from "../../common/styles/fonts";
import { H1 } from "../../common/styles/typography";
import { splitVehicleID, VEHICLE_DRIVE_GEAR_STATUS } from "../utils";
import VehicleModalText from "./VehicleModalText";
import { BLACK, GRAY_550, WHITE } from "../../common/styles/colors";
import { RootState } from "../../../store";
import { toggleVehicleRouteVisibility } from "../homeSlice";

const StyledVehicleName = styled(H1)`
    font-size: ${FONT_1};
    font-weight: bold;
    text-transform: uppercase;

    padding-right: ${SPACING_8};
`;

const StyledBattery = styled(Battery)`
    width: ${SPACING_56};
    height: auto;

    & > p {
        font-size: ${FONT_0_625};
        padding-bottom: ${SPACING_8};
        letter-spacing: ${SPACING_1};
    }

`;

const StyledVehicleSVG = styled(VehicleMarker)`
    width: ${SPACING_32};
    height: ${SPACING_32};

    margin-right: ${SPACING_12};
`;

const StyledVehicleAndIcon = styled.div`
    display: flex;
    flex-wrap: wrap;
    
    align-items: center;

    margin-left: ${SPACING_8};
`;

const StyledSpeed = styled.div`
    margin-left: ${SPACING_8};
`;

const StyledGear = styled.div`
    text-transform: uppercase;
`;

const StyledBatteryContainer = styled.div`
    padding-left: ${SPACING_8};
`;

const StyledVehicleModalText = styled(VehicleModalText)`
    tbody tr:last-of-type td:last-of-type {
        & div {
            background-color: ${WHITE};

            &::after {
                background-color: ${WHITE};
                border: solid ${SPACING_1} ${BLACK};
            }
        }
    }
`;

const StyledTooltip = withStyles(() => ({
    tooltip: {
        backgroundColor: GRAY_550,
    },
}))(Tooltip);

const StyledIconButton = styled(IconButton)`
    padding: 0;

    span {
        justify-content: flex-start;
    }

    &:focus,
    &:hover {
        background: transparent;
    }

    svg {
        color: ${BLACK};
        margin-top: ${SPACING_4};
    }
`;

export default ({
    status,
    vehicleId,
}: {
    status?: VehicleStatus;
    vehicleId: string;
}) => {
    const { driveGear, speed, batteryChargePercent } = {
        driveGear: DriveGear.UNKNOWN,
        batteryChargePercent: -1,
        ...status,
    };

    const { t } = useTranslation();
    const vehicleShortID = splitVehicleID(vehicleId);
    const dispatch = useDispatch();

    const visible = useSelector<RootState, boolean | undefined>(
        (state) => state.home.vehicleVisibility[vehicleId],
    );

    // We dont want to necessarily trigger the map to go to the vehicle
    // so we prevent default and propagation on the visibility click
    const toggleRouteVisibility = useCallback(
        (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.preventDefault();
            e.stopPropagation();
            dispatch(toggleVehicleRouteVisibility(vehicleId));
        }, [vehicleId],
    );

    return (
        <>
            <StyledVehicleAndIcon>
                <StyledVehicleSVG
                    vehicleStatusColor={VEHICLE_COLOR_MAPPING[driveGear]}
                />
                <StyledVehicleName>{vehicleShortID}</StyledVehicleName>
            </StyledVehicleAndIcon>
            <StyledGear>
                {t(VEHICLE_DRIVE_GEAR_STATUS[driveGear])}
            </StyledGear>
            <StyledSpeed>
                {speed
                    ? `${Math.round(speed)} ${t("home.vehicleStatus.vehicleSpeedUnit")}`
                    : t("home.vehicleStatus.vehicleNASpeed")}
            </StyledSpeed>
            <StyledBatteryContainer>
                <StyledBattery batteryPercentage={Math.round(batteryChargePercent)} />
            </StyledBatteryContainer>
            <StyledTooltip
                arrow
                placement="top-start"
                title={<StyledVehicleModalText status={status} vehicleID={vehicleId} />}
            >
                <InfoIcon />
            </StyledTooltip>
            <StyledIconButton
                onClick={toggleRouteVisibility}
            >
                <StyledTooltip
                    arrow
                    placement="top-start"
                    title={t(
                        visible === false
                            ? "home.vehicleStatus.routeTooltipOn"
                            : "home.vehicleStatus.routeTooltipOff",
                        { vehicleId },
                    ).toString()}
                >
                    <span>
                        {visible === false ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </span>
                </StyledTooltip>
            </StyledIconButton>
        </>
    );
};
